import React, { useCallback, useEffect, useRef } from 'react';

import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Divider, Stack } from '@lyearn/ui';
import { Link } from '@/components/Link';

import { SidebarNavSubItem } from '../types';
import { isSidebarItemActive } from '../utils';

const ROW_CLASSES = 'group h-32 flex-shrink-0 overflow-hidden truncate rounded-6';
const TRAILING_SLOT_CLASSES = 'h-32 w-32 flex-shrink-0 opacity-0';
const ICON_TILE_CLASSES = 'h-32 w-32 flex-shrink-0';

const MOVEMENT_KEYS = ['ArrowDown', 'ArrowUp', 'End', 'Home'];

const toGroups = (items: SidebarNavSubItem[]) =>
  items.reduce<SidebarNavSubItem[][]>((groups, item) => {
    const currentGroup = groups[groups.length - 1];

    if (currentGroup && currentGroup[0].groupId === item.groupId) {
      currentGroup.push(item);

      return groups;
    }

    return groups.concat([[item]]);
  }, []);

interface FlyoutPanelProps {
  currentPath: string;
  items: SidebarNavSubItem[];
  label: string;
  shouldAutoFocus?: boolean;
  triggerElement: HTMLElement | null;
  onClose: () => void;
}

const FlyoutPanel = (props: FlyoutPanelProps) => {
  const { currentPath, items, label, shouldAutoFocus, triggerElement, onClose } = props;

  const rootRef = useRef<HTMLDivElement>(null);

  const getRows = useCallback(
    () => Array.from(rootRef.current?.querySelectorAll<HTMLElement>('a[href]') ?? []),
    [],
  );

  const dismiss = useCallback(() => {
    const shouldRestoreFocus =
      !!rootRef.current?.contains(document.activeElement) ||
      document.activeElement === triggerElement;

    onClose();

    if (shouldRestoreFocus) {
      triggerElement?.focus();
    }
  }, [onClose, triggerElement]);

  useEffect(() => {
    if (shouldAutoFocus) {
      getRows()[0]?.focus();
    }
  }, [getRows, shouldAutoFocus]);

  useEffect(() => {
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };

    document.addEventListener('keydown', onDocumentKeyDown);

    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [dismiss]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        dismiss();

        return;
      }

      if (!MOVEMENT_KEYS.includes(event.key)) {
        return;
      }

      const rows = getRows();

      if (!rows.length) {
        return;
      }

      event.preventDefault();

      const currentIndex = rows.indexOf(document.activeElement as HTMLElement);
      const lastIndex = rows.length - 1;

      switch (event.key) {
        case 'ArrowDown':
          rows[currentIndex >= lastIndex ? 0 : currentIndex + 1].focus();
          break;
        case 'ArrowUp':
          rows[currentIndex <= 0 ? lastIndex : currentIndex - 1].focus();
          break;
        case 'Home':
          rows[0].focus();
          break;
        default:
          rows[lastIndex].focus();
      }
    },
    [dismiss, getRows],
  );

  const renderRow = (item: SidebarNavSubItem) => {
    const isActive = isSidebarItemActive(currentPath, item);
    const isDisabled = item.isDisabled || !item.to;

    const content = (
      <>
        <Stack
          alignItems="center"
          className={cn(
            ICON_TILE_CLASSES,
            isDisabled ? 'text-icon-placeholder' : 'text-icon-secondary',
          )}
          justifyContent="center">
          {item.icon}
        </Stack>
        <TypographyWithTooltip
          disableInteractive
          className="flex-1 truncate"
          color={isActive ? 'text-text-primary' : 'text-text-placeholder'}
          numberOfLines={1}
          variant={isActive ? 'label-02' : 'body-short-02'}>
          {item.text}
        </TypographyWithTooltip>
        <Stack aria-hidden="true" className={TRAILING_SLOT_CLASSES} />
      </>
    );

    if (isDisabled) {
      return (
        <Stack
          key={item.id}
          alignItems="center"
          aria-disabled="true"
          className={cn(ROW_CLASSES, 'pointer-events-none opacity-50')}>
          {content}
        </Stack>
      );
    }

    return (
      <Link
        key={item.id}
        className={cn(
          ROW_CLASSES,
          'flex items-center hover:bg-bg-hover',
          isActive ? 'bg-bg-hover' : undefined,
        )}
        to={item.to}
        onClick={onClose}>
        {content}
      </Link>
    );
  };

  return (
    <Stack
      ref={rootRef}
      aria-label={label}
      className="p-12"
      component="nav"
      direction="col"
      gapY={8}
      onKeyDown={onKeyDown}>
      {toGroups(items).map((group, index) => (
        <React.Fragment key={group[0].groupId ?? group[0].id}>
          {index > 0 ? <Divider className="flex-shrink-0" /> : null}
          <Stack direction="col" gapY={8}>
            {group.map(renderRow)}
          </Stack>
        </React.Fragment>
      ))}
    </Stack>
  );
};

export default FlyoutPanel;
